import type { Client } from '@openfeature/web-sdk';
import {
  OpenFeature,
  ProviderEvents,
  ProviderStatus,
} from '@openfeature/web-sdk';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { featureFlagDefinitions } from '@risksmart-app/feature-flags/src/flags';
import type { FeatureFlag, OrgFeature } from '@risksmart-app/modules/src/index';
import * as Sentry from '@sentry/react';
import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
// The leaf provider-kind module, NOT `feature-flags/openfeature`: importing the
// latter would drag the Datadog and flagd provider SDKs into the module graph of
// every one of the ~440 files that reach these hooks, most of them tests that
// never register a provider at all.
import { getActiveFeatureFlagProviderKind } from 'src/feature-flags/provider-kind';
import { E2eFlagOverridesContext } from 'src/rbac/E2eFlagOverridesContext';

// The global web-sdk client rather than the react-sdk context client:
// flag-gated components render in hundreds of test trees (and error paths)
// with no <OpenFeatureProvider> mounted, where the context hooks throw
// MissingContextError. The global client never throws — with no provider
// registered it evaluates registry defaults via the no-op provider.
const client: Client = OpenFeature.getClient();

// e2e test orgs (non-null overrides) resolve from the seeded Meta.features
// list rather than the provider — see E2eFlagOverridesContext for why. The
// REACT_APP_FEATURE_* force-enable applies on both branches.
const evaluate = (
  flag: FeatureFlag,
  e2eFlagOverrides: OrgFeature[] | null
): boolean =>
  (e2eFlagOverrides
    ? e2eFlagOverrides.includes(flag)
    : client.getBooleanValue(
        flag,
        featureFlagDefinitions[flag].defaultValue
      )) || getEnv(`REACT_APP_FEATURE_${flag}`, true) === 'true';

/**
 * Re-renders the consumer whenever flag values can have changed — provider
 * readiness, configuration updates, context changes, or the provider entering
 * an error state — so values read during render stay current.
 *
 * `Error` matters for `useAreFeatureFlagsReady`: a provider whose
 * `initialize()` rejects lands in `ERROR` and serves defaults from then on, and
 * without this event nothing re-renders, so a one-shot caller would sit out the
 * full readiness timeout for a state that is already final.
 */
const useFlagChangeSubscription = (): void => {
  const [, bumpRevision] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const events = [
      ProviderEvents.Ready,
      ProviderEvents.Error,
      ProviderEvents.ConfigurationChanged,
      ProviderEvents.ContextChanged,
    ];
    const handler = (): void => {
      bumpRevision();
    };
    events.forEach((event) => client.addHandler(event, handler));

    return () => {
      events.forEach((event) => client.removeHandler(event, handler));
    };
  }, []);
};

/**
 * Hook to check if a feature flag is enabled. Evaluated via OpenFeature
 * against the globally registered provider (`initialiseWebFeatureFlags` at
 * boot), falling back to the registry default. The `REACT_APP_FEATURE_*` env
 * var force-ENABLES a flag for local development (it cannot force-disable).
 *
 * For e2e test organisations (`Meta.isE2eTestOrg`) the org's seeded
 * `Meta.features` list is authoritative instead — flags a regression spec
 * seeds via `configureOrganisation` must be read through this hook (or its
 * lazy variant); direct `useBooleanFlagValue` reads bypass the override.
 */
export const useIsFeatureFlagEnabled = (flag: FeatureFlag): boolean => {
  const e2eFlagOverrides = useContext(E2eFlagOverridesContext);
  useFlagChangeSubscription();

  return evaluate(flag, e2eFlagOverrides);
};

/**
 * How long a one-shot caller waits for flag readiness before proceeding with
 * whatever the flag currently reads. Bounded deliberately: a provider that
 * never initialises (offline, misconfigured RUM token) must degrade to the
 * default-valued path, not hang the caller forever.
 */
const FLAG_READINESS_TIMEOUT_MS = 3000;

/**
 * ⚠️ TRANSITIONAL — DO NOT ADOPT. ⚠️
 *
 * @deprecated Transitional; not for general use. This hook exists SOLELY for the
 * Slack OAuth callback's cold-popup-load race — a one-shot effect that reads a
 * flag and latches before the provider has bound its evaluation context (see
 * `pages/slack/CallbackPage.tsx`, COI-705). It has exactly one caller and should
 * never gain another.
 *
 * If you are reaching for this to gate a component on flag readiness, you almost
 * certainly do not need it: flag-gated components that merely RENDER a flag
 * value self-correct, because `useIsFeatureFlagEnabled` re-renders on the
 * provider's readiness, configuration and context events. Blocking on this hook
 * instead trades a self-healing render for a spinner and a 3s worst case.
 *
 * Delete it together with the legacy `exchangeSlackCode` branch in
 * `hooks/useSlack.ts` when the rest-api decommission (COI-641) retires the V2
 * path and the flag branch goes with it.
 *
 * ---
 *
 * Reports whether a flag read is SETTLED — i.e. whether the value
 * `useIsFeatureFlagEnabled` returns right now can still change from "default"
 * to "targeted".
 *
 * Almost no caller needs this. `useIsFeatureFlagEnabled` re-renders on
 * readiness, configuration and context changes, so a component that simply
 * renders the current value self-corrects. It matters only where a flag is read
 * ONCE and the decision cannot be revised — a first-mount effect that fires a
 * request and latches (COI-705: the Slack OAuth callback page). There the
 * provider is still `NOT_READY` and, critically, the evaluation context has no
 * `targetingKey` yet: `FeatureFlagProvider` binds `orgId` from an ANCESTOR's
 * async effect, and React flushes effects child-first, so a cold popup load
 * would read the registry default and latch it forever.
 *
 * Ready means any of:
 *  - e2e overrides are in play — they bypass the provider entirely;
 *  - no provider is registered — registry defaults are already final
 *    (also the common case in test trees);
 *  - the provider has settled (anything but `NOT_READY`, so an `ERROR`
 *    provider unblocks on its default values) AND the evaluation context
 *    carries the `orgId` targeting key every rule keys off;
 *  - {@link FLAG_READINESS_TIMEOUT_MS} elapsed — the bounded fallback.
 */
export const useAreFeatureFlagsReady = (): boolean => {
  const e2eFlagOverrides = useContext(E2eFlagOverridesContext);
  useFlagChangeSubscription();
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Derived BEFORE the effect so the timer is only ever armed while readiness is
  // genuinely unresolved. Arming it unconditionally would fire the warning below
  // 3s after every already-ready mount — a false alarm on the happy path.
  const isResolved =
    Boolean(e2eFlagOverrides) ||
    getActiveFeatureFlagProviderKind() === null ||
    (client.providerStatus !== ProviderStatus.NOT_READY &&
      Boolean(OpenFeature.getContext().targetingKey));

  useEffect(() => {
    if (isResolved) {
      return;
    }

    const timer = setTimeout(() => {
      // Reaching the timeout means the provider never settled or never bound the
      // org context, so the caller is about to act on DEFAULT flag values
      // without knowing it — the same invisible-failure shape as the bug this
      // hook exists to prevent, just 3s later. Report it rather than let it pass
      // silently.
      Sentry.captureMessage(
        'Feature flags did not become ready before the timeout; proceeding with default values',
        { level: 'warning', tags: { component: 'feature-flags' } }
      );
      setHasTimedOut(true);
    }, FLAG_READINESS_TIMEOUT_MS);

    // Readiness arriving re-runs this effect and clears the pending timer, so a
    // page that becomes ready at t+2.9s never reports.
    return () => clearTimeout(timer);
  }, [isResolved]);

  return isResolved || hasTimedOut;
};

/**
 * Lazy variant that returns a callback for checking feature flags. Useful when
 * you need to check multiple flags or pass the check as a callback. The
 * callback reads the live flag value on each invocation.
 */
export const useIsFeatureFlagEnabledLazy = (): ((
  flag: FeatureFlag
) => boolean) => {
  const e2eFlagOverrides = useContext(E2eFlagOverridesContext);
  useFlagChangeSubscription();

  return useCallback(
    (flag: FeatureFlag) => evaluate(flag, e2eFlagOverrides),
    [e2eFlagOverrides]
  );
};
